// src/pages/BizManagePage.jsx
import React from "react";
import { Plus, ChevronDown, FileSpreadsheet, Building2, Edit2, Trash2 } from "lucide-react";
import { useBizManagePage } from "@hooks";
import { BizExcelUploadModal, BizEditModal, PageHeader, FilterBar, DataTable } from "@components"; 
import { BizCreateModal } from "./BizCreateModal";
import { BizDetailModal } from "./BizDetailModal";

export function BizManagePage() {
  const [isDetailModalOpen, setIsDetailModalOpen] = React.useState(false);
  const [detailData, setDetailData] = React.useState(null);

  const {
    filters, setFilters, page, onSearch,
    isExcelModalOpen, setIsExcelModalOpen, isUploading, handleExcelUpload,
    selectedIds, toggleSelectRow, isSelected, anyChecked,
    isEditModalOpen, setIsEditModalOpen, handleEditClick, handleUpdateBiz, isUpdating,
    handleDeleteClick, isDeleting,
    isCreateModalOpen, setIsCreateModalOpen, handleCreateBiz, isCreating, auth
  } = useBizManagePage();

  const handleReset = () => {
    setFilters({ branch: '', bizName: '', bizRegNo: '', pageNum: 1 });
  };

  const selectedRowData = selectedIds.length === 1 
    ? page.rows?.find(r => r.bizMgtNo === selectedIds[0]) || null 
    : null;

  const initialHelpText = `배정의 기초가 되는 전체 사업장 마스터 데이터를 조회하고 관리하는 페이지입니다.

[주요 기능 안내]
1. 신규 등록: [신규 등록] 버튼을 눌러 개별 사업장을 직접 입력하여 추가할 수 있습니다.
2. 정보 수정: 표의 왼쪽 체크박스(☑️)를 선택한 후, [정보 수정] 버튼을 눌러 담당자, 근로자 수 등을 수정합니다.
3. 삭제: 체크박스를 선택한 후 [삭제] 버튼을 누르면 목록에서 제거됩니다.
4. 엑셀 업로드: 대량의 사업장 데이터를 한 번에 등록/수정할 때 사용합니다.

[유의사항]
기존에 등록된 '사업자등록번호'와 '사업장관리번호'가 동일하다면 해당 사업장의 정보가 덮어쓰기(업데이트) 됩니다.`;

const formatBizRegNo = (bizRegNo) => {
    if (!bizRegNo) return "-";
    const cleaned = bizRegNo.replace(/[^0-9]/g, ""); 
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{2})(\d{5})/, "$1-$2-$3");
    }
    return bizRegNo; 
  };

  const tableColumns = [
    { label: "선택", align: "text-center", width: "w-10" },
    { label: "사업자등록번호" },
    { label: "사업장명", className: "min-w-[200px]" },
    { label: "대표자" },
    { label: "근로자수", align: "text-right" },
    { label: "소재지 (주소)", className: "min-w-[250px]" },
    { label: "현장 연락처" },
    { label: "담당자" },
    { label: "등록지사" }
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader 
        icon={Building2} 
        iconBgColor="bg-blue-600"
        title="사업장 DB 관리" 
        description="배정의 기초가 되는 전체 사업장 마스터 데이터를 조회하고 관리합니다." 
        auth={auth}                   
        helpId="biz_manage"           
        initialHelpText={initialHelpText} 
      />

      <FilterBar
        onReset={handleReset}
        actions={
          <>
            <button 
              onClick={() => setIsCreateModalOpen(true)}
              className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              <Plus className="h-4 w-4 text-blue-600" /> 신규 등록
            </button>
            <button 
              onClick={handleEditClick}
              disabled={selectedIds.length !== 1} 
              className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Edit2 className="h-4 w-4 text-orange-500" /> 정보 수정
            </button>
            <button 
              onClick={handleDeleteClick}
              disabled={!anyChecked || isDeleting}
              className="flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors disabled:cursor-not-allowed"
            >
              <Trash2 className="h-4 w-4" /> 삭제 {anyChecked && `(${selectedIds.length})`}
            </button>
            <button 
              onClick={() => setIsExcelModalOpen(true)} 
              className="flex h-10 items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 text-sm font-medium text-green-700 hover:bg-green-100 transition-colors"
            >
              <FileSpreadsheet className="h-4 w-4" /> 엑셀 업로드
            </button>
          </>
        }
      >
        <div className="space-y-1.5 flex-1 max-w-[200px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">등록 지사</label>
          <div className="relative">
            <select 
              className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
              value={filters.branch}
              onChange={(e) => setFilters(p => ({ ...p, branch: e.target.value }))}
            >
              <option value="">전체 지사</option>
              <option value="서울광역본부">서울광역본부</option>
              <option value="강원지역본부">강원지역본부</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="space-y-1.5 flex-1 max-w-xs">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">사업장명</label>
          <input
            type="text"
            placeholder="사업장명 입력"
            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={filters.bizName}
            onChange={(e) => setFilters(p => ({ ...p, bizName: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          />
        </div>

        <div className="space-y-1.5 flex-1 max-w-xs">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">사업자등록번호</label>
          <input
            type="text"
            placeholder="번호 입력 (- 제외)"
            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={filters.bizRegNo}
            onChange={(e) => setFilters(p => ({ ...p, bizRegNo: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          />
        </div>
      </FilterBar>

      <DataTable 
        columns={tableColumns}
        page={page}
        itemName="사업장 데이터"
        onPageChange={(newPage) => setFilters(p => ({ ...p, pageNum: newPage }))}
        emptyMessage="등록된 사업장 데이터가 없습니다."
        renderRow={(row, idx) => {
          const checked = isSelected(row.bizMgtNo); 
          return (
            <tr 
              key={idx} 
              onClick={() => toggleSelectRow(row.bizMgtNo, !checked)} 
              className={`transition-colors cursor-pointer ${checked ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}
            >
              <td className="px-6 py-4 text-center">
                <input 
                  type="checkbox" 
                  checked={checked}
                  onChange={(e) => toggleSelectRow(row.bizMgtNo, e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  onClick={(e) => e.stopPropagation()} 
                />
              </td>
              <td className="px-6 py-4 font-medium text-slate-700 tracking-wide">{formatBizRegNo(row.bizRegNo)}</td>
              <td 
                className="px-6 py-4 font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setDetailData(row);
                  setIsDetailModalOpen(true);
                }}
              >
                {row.bizName}
              </td>
              <td className="px-6 py-4 text-slate-600">{row.ceoName}</td>
              <td className="px-6 py-4 text-slate-600 text-right">{row.employeeCount?.toLocaleString()}명</td>
              <td className="px-6 py-4 text-slate-500 truncate max-w-[250px]" title={row.address1}>{row.address1}</td>
              <td className="px-6 py-4 text-slate-600">{row.bizPhone || '-'}</td>
              <td className="px-6 py-4 text-slate-600">{row.managerName || '-'}</td>
              <td className="px-6 py-4">
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                  {row.regBranch}
                </span>
              </td>
            </tr>
          );
        }}
      />

      <BizExcelUploadModal 
        isOpen={isExcelModalOpen} 
        onClose={() => setIsExcelModalOpen(false)} 
        onUpload={handleExcelUpload}
        isUploading={isUploading}
      />

      <BizCreateModal 
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateBiz}
        isSaving={isCreating}
      />

      <BizEditModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleUpdateBiz}
        isUpdating={isUpdating}
        selectedData={selectedRowData}
      />

      <BizDetailModal 
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        detailData={detailData}
      />
    </div>
  );
}